// useFeatureGate — CONTRATO + FÁBRICA del gate de una feature del rollout.
//
// Problema que resuelve: la elegibilidad REAL de una feature no es solo la asignación del
// rollout (variante/invite_surface). Cada feature suma condiciones propias — compatibilidad
// del dispositivo (impresion-ble), módulo contratado (menu-v2 en el navbar), plataforma,
// permisos… Si cada superficie (navbar, sección, coachmark, página) re-deriva ese cruce por
// su cuenta, a la tercera feature hay inconsistencias garantizadas ("el navbar invita pero
// la sección no aparece").
//
// Diseño: cada feature define su gate UNA sola vez con `makeFeatureGateHook` y TODAS las
// superficies consumen el hook resultante. Las condiciones extra viajan como un hook
// (`useInvitable`) porque suelen necesitar React (useModules, contexto) — un registro
// dinámico de funciones no podría usarlas sin romper las reglas de hooks. Un hook POR
// feature implementando el MISMO contrato da type-safety, tree-shaking y cero lookup mágico.
//
// Reparto de responsabilidades (importante para no sobre-gatear):
//   - `inviteEligible`/`showInvite`: el cruce COMPLETO (rollout + condiciones extra). Es lo
//     que consumen etiquetas "Nuevo", dots y coachmarks — nunca invitar donde no se puede usar.
//   - `offered`: SOLO flag de build + variante (treatment/GA). Las condiciones de RUNTIME del
//     render (p.ej. "este navegador soporta BT") se quedan en la superficie, que decide si
//     muestra la funcionalidad o un aviso explicativo — ese matiz es de producto, no del gate.

import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { isFeatureBuildEnabled } from '@utils/featureFlags';
import type { FeatureKey } from '@utils/featureRollout/constants';

export interface FeatureGate {
  /** El comercio tiene la funcionalidad (flag de build + treatment/GA). */
  offered: boolean;
  /** Control invitable del rollout + condiciones extra de la feature → etiquetas "Nuevo". */
  inviteEligible: boolean;
  /** inviteEligible + el backend autoriza invitar AHORA (invite_surface, cadencia server-side). */
  showInvite: boolean;
  /** Registrar exhibición de la invitación (dedupe por sesión en el framework). */
  shown: () => void;
  /** Aceptar la invitación: switch optimista a treatment (outbox). */
  accept: (trigger?: string) => void;
  /** "Ahora no": snooze server-side (la cadencia de re-invitación vive en el backend). */
  dismiss: () => void;
  /**
   * Reporta un error de render/runtime de la superficie TREATMENT gateada como
   * behavior='error' (materia prima del guardrail max_error_rate_pct). Fire-and-forget;
   * el hook base ya early-returna fuera de treatment. Para errores de superficies que
   * viven en CONTROL (p.ej. el coachmark de invitación) NO usar esto — van directo a
   * Sentry, no contaminan el error_rate del brazo treatment.
   */
  emitError: (error: unknown, trigger?: string) => void;
}

export interface FeatureGateConfig {
  // Union tipado (SPEC-8): un slug con typo no compila (sin esto, crearía una feature
  // fantasma con cache/telemetría propios y GETs fallidos silenciosos).
  featureKey: FeatureKey;
  /** trigger_source con el que viajan shown/accept/dismiss (p.ej. 'ble_coachmark'). */
  inviteTrigger: string;
  /** Días de snooze al descartar la invitación. Default 7 (mismo que menu-v2). */
  snoozeDays?: number;
  /**
   * Condiciones EXTRA de invitación de la feature, evaluadas como hook (pueden usar otros
   * hooks: módulos, contexto, capacidades del dispositivo). `true` = este dispositivo/sesión
   * puede ser invitado. Default: sin condiciones extra (siempre invitable si el rollout lo es).
   */
  useInvitable?: () => boolean;
}

// Crea el hook de gate de una feature. Llamar UNA vez a nivel módulo por feature:
//   export const useMiFeatureGate = makeFeatureGateHook({ featureKey: ..., ... });
export function makeFeatureGateHook(config: FeatureGateConfig): () => FeatureGate {
  const { featureKey, inviteTrigger, snoozeDays = 7, useInvitable = () => true } = config;
  return function useFeatureGate(): FeatureGate {
    const fv = useFeatureVersion(featureKey);
    const flagOn = isFeatureBuildEnabled(featureKey);
    const invitable = useInvitable();
    const offered = flagOn && (fv.variant === 'treatment' || fv.gaForced);
    const inviteEligible = flagOn && invitable && fv.isEligibleInvitation;
    return {
      offered,
      inviteEligible,
      showInvite: inviteEligible && fv.inviteSurface !== 'none',
      shown: () => fv.markInvitationShown(inviteTrigger),
      // pushInvite: el accept de una superficie de invitación ES una adopción-push →
      // invitation_accepted se emite estructuralmente para toda feature del contrato (SPEC-1).
      accept: (trigger = inviteTrigger) => fv.switchTo('treatment', trigger, { pushInvite: true }),
      dismiss: () => fv.snooze(snoozeDays, inviteTrigger),
      emitError: (error, trigger) => fv.emitError(error, trigger),
    };
  };
}
